import Cookies from 'js-cookie';
import { call, put, takeLatest, all } from 'redux-saga/effects';

import { logEvent } from 'common/helpers/internalAnalytics';
import { customerId } from 'common/helpers/tokenHelper';
import { addAPIError, removeAPIError } from 'redux/apiErrors/sagas';
import { get as getCart } from 'redux/cart/sagas';
import { get as getClubSignup } from 'redux/clubSignup/sagas';
import { get as getReservationCart } from 'redux/reservationCart/sagas';

import {
  getCustomer,
  loginCustomer,
  fbLoginOrCreateCustomer,
  googleLoginOrCreateCustomer,
  addCustomer,
  editCustomer,
  editCustomerPassword,
  forgotPassword,
  resetPassword,
  validateResetPasswordId,
  magicLink,
  validateMagicLinkId,
  validateAccountActivationId,
  logoutCustomer,
  unsubscribeCustomer,
  deleteCustomer
} from './api';
import {
  GET_CUSTOMER_REQUEST,
  GET_CUSTOMER_OBJECT_REQUESTING,
  GET_CUSTOMER_SUCCESS,
  ADD_CUSTOMER_REQUEST,
  EDIT_CUSTOMER_REQUEST,
  EDIT_PASSWORD_REQUEST,
  LOGIN_REQUEST,
  FB_LOGIN_OR_CREATE_REQUEST,
  GOOGLE_LOGIN_OR_CREATE_REQUEST,
  LOGOUT_REQUEST,
  FORGOT_PASSWORD_REQUEST,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_ID_REQUEST,
  MAGIC_LINK_REQUEST,
  DELETE_CUSTOMER_REQUEST,
  MAGIC_LINK_ID_REQUEST,
  ACCOUNT_ACTIVATION_ID_REQUEST,
  UNSUBSCRIBE_CUSTOMER_REQUEST
} from './constants';

function* get() {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_OBJECT_REQUESTING });
    if (!customerId()) {
      yield put({ type: GET_CUSTOMER_SUCCESS, payload: null });
    } else {
      const response = yield call(getCustomer);

      yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      Cookies.remove('customerToken');
      yield put({ type: GET_CUSTOMER_SUCCESS, payload: null });
    }
    yield call(addAPIError, error);
  }
}

function* add(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_OBJECT_REQUESTING });
    const { customer } = action;
    const response = yield call(addCustomer, customer);
    const { data } = response;
    if (data.isAccountActivationSent) {
      // Means its an existing customer
      action.onSuccess(response.data);
    } else {
      yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
      const newAction = {
        email: action.customer.email,
        password: action.customer.password
      };
      yield call(login, newAction);
      if (action.onSuccess) {
        action.onSuccess(response.data);
      }
    }
  } catch (error) {
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError(error);
    }
  }
}

function* edit(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_OBJECT_REQUESTING });
    const response = yield call(editCustomer, customerId(), action.params);
    yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError(error);
    }
  }
}

function* editPassword(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_OBJECT_REQUESTING });
    const response = yield call(
      editCustomerPassword,
      customerId(),
      action.params
    );
    yield put({ type: GET_CUSTOMER_SUCCESS, payload: response.data });
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError(error);
    }
  }
}

function* login(action) {
  const loginObject = {
    email: action.email,
    password: action.password
  };
  yield call(consolidatedLogin, action, loginObject, 'basic');
}

function* fbLoginOrCreate(action) {
  const loginObject = {
    fbAccessToken: action.accessToken
  };
  yield call(consolidatedLogin, action, loginObject, 'facebook');
}

function* googleLoginOrCreate(action) {
  const loginObject = {
    googleTokenId: action.tokenId
  };
  yield call(consolidatedLogin, action, loginObject, 'google');
}

function* magicLinkIdValid(action) {
  const loginObject = {
    magicLinkId: action.magicLinkId
  };
  yield call(consolidatedLogin, action, loginObject, 'magicLink');
}

function* accountActivationIdValid(action) {
  const loginObject = {
    accountActivationId: action.accountActivationId
  };
  yield call(consolidatedLogin, action, loginObject, 'accountActivation');
}

function* resetPasswordIdValid(action) {
  const loginObject = {
    resetPasswordId: action.resetPasswordId
  };
  yield call(consolidatedLogin, action, loginObject, 'resetPassword');
}

function* consolidatedLogin(action, loginObj, type) {
  try {
    yield call(removeAPIError);
    const loginObject = loginObj;
    if (cartId()) {
      loginObject.cartId = cartId();
    }
    if (clubSignupId()) {
      loginObject.clubSignupId = clubSignupId();
    }
    if (reservationCartId()) {
      loginObject.reservationCartId = reservationCartId();
    }
    let response;
    switch (type) {
      case 'facebook': {
        logEvent('Login', 'Facebook');
        response = yield call(fbLoginOrCreateCustomer, loginObject);
        break;
      }
      case 'google': {
        logEvent('Login', 'Google');
        response = yield call(googleLoginOrCreateCustomer, loginObject);
        break;
      }
      case 'magicLink': {
        logEvent('Login', 'Magic Link');
        response = yield call(validateMagicLinkId, loginObject);
        break;
      }
      case 'accountActivation': {
        logEvent('Login', 'Account Activation');
        response = yield call(validateAccountActivationId, loginObject);
        break;
      }
      case 'resetPassword': {
        logEvent('Login', 'Reset Password');
        response = yield call(validateResetPasswordId, loginObject);
        break;
      }
      case 'basic': {
        logEvent('Login', 'Basic Auth');
        response = yield call(loginCustomer, loginObject);
        break;
      }
      default: {
        throw new Error('Invalid Login Type');
      }
    }
    Cookies.set('customerToken', response.data.token, {
      expires: 365,
      secure: process.env.APP_MODE === 'production'
    });
    yield call(get);
    if (cartId()) {
      yield call(getCart);
    }
    if (clubSignupId()) {
      yield call(getClubSignup);
    }
    if (reservationCartId()) {
      yield call(getReservationCart);
    }
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 404)
    ) {
      action.onError();
    } else {
      yield call(addAPIError, error);
    }
  }
}

function* forgot(action) {
  try {
    yield call(removeAPIError);
    const forgotObject = {
      email: action.data.email
    };
    yield call(forgotPassword, forgotObject);
    if (action.onSuccess) {
      action.onSuccess();
    }
  } catch (error) {
    if (error.response && error.response.status === 404 && action.onError) {
      action.onError();
    } else {
      yield call(addAPIError, error);
    }
  }
}

function* magic(action) {
  try {
    yield call(removeAPIError);
    const magicObject = {
      email: action.data.email,
      redirectLink: action.data.redirectLink
    };
    yield call(magicLink, magicObject);
    if (action.onSuccess) {
      action.onSuccess();
    }
  } catch (error) {
    if (error.response && error.response.status === 404 && action.onError) {
      action.onError();
    } else {
      yield call(addAPIError, error);
    }
  }
}

function* reset(action) {
  try {
    yield call(removeAPIError);
    const response = yield call(resetPassword, action.data);
    Cookies.set('customerToken', response.data.token, {
      expires: 365,
      secure: process.env.APP_MODE === 'production'
    });
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError(error);
    }
  }
}

function* unsubscribe(action) {
  try {
    yield call(removeAPIError);
    const response = yield call(
      unsubscribeCustomer,
      action.customerId,
      action.payload
    );
    action.onSuccess(response.data);
  } catch (error) {
    yield call(addAPIError, error);
  }
}

function* logout() {
  yield call(removeAPIError);
  try {
    yield call(logoutCustomer);
  } catch {
    // Fail silenetly
  }
  Cookies.remove(`customerToken`);
  Cookies.remove(`cartId`);
  Cookies.remove(`clubSignupId`);
  Cookies.remove(`reservationCartId`);
  window.location.href = '/';
  yield call(getCart);
}

function* deleteObj(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CUSTOMER_OBJECT_REQUESTING });
    const response = yield call(deleteCustomer);
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
    yield call(logout);
  } catch (error) {
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError(error);
    }
  }
}

function* watchList() {
  yield takeLatest(ADD_CUSTOMER_REQUEST, add);
  //   yield takeLatest(ADD_CUSTOMER_WITH_ADDRESS_REQUEST, addWithAddress);
  yield takeLatest(EDIT_CUSTOMER_REQUEST, edit);
  yield takeLatest(EDIT_PASSWORD_REQUEST, editPassword);
  yield takeLatest(GET_CUSTOMER_REQUEST, get);
  //   yield takeLatest(GET_CUSTOMER_WITH_DEFAULTS_REQUEST, getWithDefault);
  yield takeLatest(LOGIN_REQUEST, login);
  yield takeLatest(FB_LOGIN_OR_CREATE_REQUEST, fbLoginOrCreate);
  yield takeLatest(GOOGLE_LOGIN_OR_CREATE_REQUEST, googleLoginOrCreate);
  yield takeLatest(FORGOT_PASSWORD_REQUEST, forgot);
  yield takeLatest(RESET_PASSWORD_ID_REQUEST, resetPasswordIdValid);
  yield takeLatest(RESET_PASSWORD_REQUEST, reset);
  yield takeLatest(MAGIC_LINK_REQUEST, magic);
  yield takeLatest(MAGIC_LINK_ID_REQUEST, magicLinkIdValid);
  yield takeLatest(ACCOUNT_ACTIVATION_ID_REQUEST, accountActivationIdValid);
  yield takeLatest(LOGOUT_REQUEST, logout);
  yield takeLatest(UNSUBSCRIBE_CUSTOMER_REQUEST, unsubscribe);
  yield takeLatest(DELETE_CUSTOMER_REQUEST, deleteObj);
}

export default function* customerSagas() {
  yield all([watchList()]);
}

const cartId = () => Cookies.get('cartId');
const clubSignupId = () => Cookies.get('clubSignupId');
const reservationCartId = () => Cookies.get('reservationCartId');
